/*!
 * Copyright © 2026. Verizon Connect Ireland Limited. All rights reserved.
 */

import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { IEnvironmentConfig } from '../../../../environments/environment.interface';
import { APP_CONFIG } from '../../../core/config/app-config.token';
import { WindowResizeService } from '../../../core/services';
import { getShouldOffsetForSidePanel } from '../../../core/store/selectors';
import { IAppState } from '../../../core/store/state';
import { IDeviceHealthCheckLiveChatParams } from '../../../device-health-check/models';

/** Host element rendered by the widget script in the light DOM (confirmed via DevTools). */
const CHAT_WIDGET_HOST_SELECTOR = 'vzc-chat-widget';
/** Element inside the widget's open shadow root that carries the fixed `right` positioning. */
const CHAT_WIDGET_INNER_SELECTOR = '.chat-widget';
/**
 * The FAB is rendered as a direct child of `.chat-widget` only while the widget is minimized
 * (closed). Its presence is how we detect the closed state; its absence means the full chat panel
 * is open instead. Unlike the open panel, the FAB's position is fixed at `CLOSED_WIDGET_RIGHT_PX`
 * regardless of the side panel's state (see `_applyOffsetToCurrentElements()`).
 */
const CHAT_WIDGET_FAB_SELECTOR = '.chat-widget > vzc-floating-action-button';
/** Offset applied to the open chat panel so it clears the side panel while it's open on desktop. */
const SIDE_PANEL_OFFSET_RIGHT_PX = '375px';
/** Fixed offset applied to the minimized FAB at all times, independent of the side panel's state. */
const CLOSED_WIDGET_RIGHT_PX = '196px';
const CLOSED_WIDGET_BOTTOM_PX = '6PX';

/**
 * Service for loading and managing the Agent Core chat widget (`window.__vzcChatLoader`).
 *
 * Implementation Pattern:
 * - A single lightweight "loader" script (`loaderUrl`) is fetched once and exposes
 *   `window.__vzcChatLoader`, which in turn is used to `init()` and `load()` the actual
 *   widget script (`scriptUrl`).
 * - `init()` must be called before `load()` so the widget knows its endpoint/auth/locale
 *   configuration up front.
 * - Locale-specific URLs use a `{{locale}}` placeholder that is resolved from the document's
 *   <base href> at runtime (see angular.json i18n.locales[].baseHref), falling back to "en-US".
 * - `unload()` destroys the widget and resets `isLoaded$` so a subsequent `load()` can run again.
 * - Positioning relative to the side panel CANNOT be done via CSS: the widget renders its
 *   positioned element (`.chat-widget`) inside an open shadow root under `<vzc-chat-widget>`, and
 *   regular stylesheets cannot cross a shadow boundary (no `::shadow`/`/deep/` combinators exist in
 *   current browsers), and the widget does not expose any CSS custom properties either. The only
 *   way in is JS reaching directly into the (open) shadow root and setting inline styles on the
 *   actual element — see `_watchSidePanelOffset()`. This also sidesteps `init()`'s one-shot config:
 *   `init()` only runs once, so it can't react to the side panel opening/closing afterward anyway.
 *   This service (and the widget it loads) is a long-lived singleton that outlives the side
 *   panel's own component tree, so the widget stays open and simply repositions when the panel
 *   closes rather than being destroyed with it.
 *
 * ASSUMPTION (needs verification): the `__vzcChatLoader.init()` API documented so far only
 * exposes `endpoint`, `scriptUrl`, `authHost`, `authClientId`, `inline` and `endChatCallback`.
 * There is no confirmed field for passing contact metadata (unlike Amazon Connect's
 * `contactAttributes`). This service passes the metadata via a best-guess `initialPrompt` field
 * on the `init()` payload. Verify this against the real widget's documented API once available
 * and adjust/remove the field if the name or mechanism differs.
 */
@Injectable()
export class AgentCoreChatLoaderService {
  public readonly isLoaded$ = new BehaviorSubject(false);

  private readonly _settings: IEnvironmentConfig['deviceHealthCheckAgentCoreChat'];

  private _hostObserver: MutationObserver | null = null;
  private _shadowObserver: MutationObserver | null = null;
  private _pendingShouldOffset = false;
  private _pendingIsMobileOrTablet = false;

  constructor(
    @Inject(APP_CONFIG) private readonly _envConfig: IEnvironmentConfig,
    @Inject(DOCUMENT) private readonly _document: Document,
    private readonly _store: Store<IAppState>,
    private readonly _windowResizeService: WindowResizeService
  ) {
    this._settings = this._envConfig.deviceHealthCheckAgentCoreChat;
    this._watchSidePanelOffset();
  }

  public load(params: IDeviceHealthCheckLiveChatParams): void {
    if (this.isLoaded$.value) {
      return;
    }

    this._loadLoaderScript$()
      .pipe(tap(() => this._initWidget(params)))
      .subscribe({
        error: error => console.error(error)
      });
  }

  public unload(): void {
    const window = this._document.defaultView as any;
    window.__vzcChatLoader?.destroy();
    this.isLoaded$.next(false);
    this._hostObserver?.disconnect();
    this._hostObserver = null;
    this._shadowObserver?.disconnect();
    this._shadowObserver = null;
  }

  private _initWidget(params: IDeviceHealthCheckLiveChatParams): void {
    const window = this._document.defaultView as any;

    window.__vzcChatLoader.init({
      endpoint: this._settings.endpoint,
      scriptUrl: this._resolveLocaleUrl(this._settings.scriptUrl),
      authHost: this._settings.authHost,
      authClientId: this._settings.authClientId,
      // See ASSUMPTION note in the class doc comment above.
      initialPrompt: this._buildInitialPrompt(this._buildContactAttributes(params)),
      onChatEnded: () => this.unload(),
      panel: {
        height: 'calc(100vh - 110px)'
      }
      // Deliberately no `position`/`panel` here: those are static and set once at init time, so
      // they can't react to the side panel opening/closing later. Positioning relative to the
      // side panel is handled by reaching into the shadow root directly instead (see
      // _watchSidePanelOffset() and the class doc comment above).
    });
    window.__vzcChatLoader.open();

    this.isLoaded$.next(true);
  }

  /**
   * Keeps the widget's inline `right`/`bottom` positioning (set directly on the elements inside
   * its shadow root, since CSS can't reach it — see class doc comment) in sync with
   * `shouldOffsetForSidePanel` and the current breakpoint while the widget is loaded. Neither
   * offset (the open panel's side-panel offset, nor the minimized FAB's fixed offset) applies on
   * mobile/tablet — both are desktop-only repositioning concerns. This subscription is never torn
   * down because the service itself is a long-lived singleton for the lifetime of the app.
   */
  private _watchSidePanelOffset(): void {
    combineLatest([
      this._store.pipe(select(getShouldOffsetForSidePanel)),
      this._windowResizeService.isMobileOrTablet$,
      this.isLoaded$
    ]).subscribe(([shouldOffsetForSidePanel, isMobileOrTablet, isLoaded]) => {
      if (!isLoaded) {
        return;
      }
      this._applyOffset(shouldOffsetForSidePanel && !isMobileOrTablet, isMobileOrTablet);
    });
  }

  /**
   * Applies (or clears) positioning on whichever of the widget's positioned elements currently
   * exist in the shadow root (the full chat panel `.chat-widget`, and/or the minimized FAB
   * `.chat-widget > vzc-floating-action-button`). The widget toggles between these two states
   * internally (open panel vs. minimized FAB) by adding/removing the FAB from the DOM, so this
   * keeps re-applying the *latest* desired state via a persistent shadow-root observer rather
   * than a one-shot lookup, otherwise a panel-closed -> FAB-minimized transition (or vice versa)
   * after the offset was already applied once would leave the newly-shown element unpositioned.
   */
  private _applyOffset(shouldOffsetForSidePanel: boolean, isMobileOrTablet: boolean): void {
    this._pendingShouldOffset = shouldOffsetForSidePanel;
    this._pendingIsMobileOrTablet = isMobileOrTablet;

    const host = this._document.querySelector(CHAT_WIDGET_HOST_SELECTOR);
    if (host && this._applyOffsetToCurrentElements(host, shouldOffsetForSidePanel, isMobileOrTablet)) {
      this._watchShadowRootForOffset(host);
      return;
    }

    this._waitForChatWidgetHost();
  }

  /**
   * Sets/clears positioning on every positioned element currently present under the host's shadow
   * root (chat panel and/or FAB). Returns true if at least one element was found, so callers know
   * whether it's still worth watching this host for further changes.
   *
   * The two elements are positioned independently, and neither is repositioned on mobile/tablet:
   * - `.chat-widget` (open panel) toggles between the widget's default position and
   *   `SIDE_PANEL_OFFSET_RIGHT_PX` based on `shouldOffsetForSidePanel`, so it clears the side panel
   *   while open, on desktop only.
   * - The FAB (minimized/closed state) always sits at the fixed `CLOSED_WIDGET_RIGHT_PX`/
   *   `CLOSED_WIDGET_BOTTOM_PX` on desktop, regardless of the side panel's state.
   */
  private _applyOffsetToCurrentElements(host: Element, shouldOffsetForSidePanel: boolean, isMobileOrTablet: boolean): boolean {
    const innerElement = host.shadowRoot?.querySelector(CHAT_WIDGET_INNER_SELECTOR) as HTMLElement;
    const fabElement = host.shadowRoot?.querySelector(CHAT_WIDGET_FAB_SELECTOR) as HTMLElement;

    innerElement?.style.setProperty('right', !isMobileOrTablet && shouldOffsetForSidePanel ? SIDE_PANEL_OFFSET_RIGHT_PX : null);

    if (isMobileOrTablet) {
      fabElement?.style.removeProperty('position');
      fabElement?.style.removeProperty('right');
      fabElement?.style.removeProperty('bottom');
    } else {
      fabElement?.style.setProperty('position', 'relative');
      fabElement?.style.setProperty('right', CLOSED_WIDGET_RIGHT_PX);
      fabElement?.style.setProperty('bottom', CLOSED_WIDGET_BOTTOM_PX);
    }

    return !!innerElement || !!fabElement;
  }

  /**
   * Watches the host's shadow root for structural changes (e.g. panel <-> FAB toggling) and
   * re-applies the most recently requested state (`_pendingShouldOffset`/`_pendingIsMobileOrTablet`)
   * to whichever elements are present after each change. Runs for as long as the widget stays
   * loaded; torn down in `unload()`.
   */
  private _watchShadowRootForOffset(host: Element): void {
    if (this._shadowObserver || !host.shadowRoot) {
      return;
    }

    this._shadowObserver = new MutationObserver(() => {
      this._applyOffsetToCurrentElements(host, this._pendingShouldOffset, this._pendingIsMobileOrTablet);
    });
    this._shadowObserver.observe(host.shadowRoot, { childList: true, subtree: true });
  }

  /**
   * Watches for the widget host to be added to the document (and, once present, for its shadow
   * root to be attached), then applies the pending state and starts the persistent shadow-root
   * observer. Used when positioning is requested before the widget has finished rendering.
   */
  private _waitForChatWidgetHost(): void {
    if (this._hostObserver) {
      return;
    }

    const tryResolveHost = (): boolean => {
      const host = this._document.querySelector(CHAT_WIDGET_HOST_SELECTOR);
      if (!host?.shadowRoot) {
        return false;
      }
      this._hostObserver?.disconnect();
      this._hostObserver = null;
      this._applyOffsetToCurrentElements(host, this._pendingShouldOffset, this._pendingIsMobileOrTablet);
      this._watchShadowRootForOffset(host);
      return true;
    };

    if (tryResolveHost()) {
      return;
    }

    this._hostObserver = new MutationObserver(() => tryResolveHost());
    this._hostObserver.observe(this._document.body, { childList: true, subtree: true });
  }

  private _buildContactAttributes(params: IDeviceHealthCheckLiveChatParams): Record<string, string> {
    return {
      accountId: params.accountId,
      vehicleName: params.vehicleName,
      trackerType: params.trackerType,
      esn: params.ESN,
      lastUpdate: params.lastUpdate,
      status: params.status
    };
  }

  private _buildInitialPrompt(params: Record<string, string>): string {
    const jsonString = JSON.stringify(params);
    return `{
            "ENTRY_POINT":"LIVE_MAP_PING_RESTART",
            "CONTACT_ATTRIBUTES": ${jsonString}
            }
Your first response for me should be a detailed list of 'CONTACT_ATTRIBUTES' what
I have sent, and after that connect me to a human agent.`;
  }

  /**
   * Substitutes the "{{locale}}" placeholder in a config URL (e.g. scriptUrl, loaderUrl) with the
   * actual locale segment for this build, so a single environment config value can serve every
   * locale build without per-locale environment files.
   */
  private _resolveLocaleUrl(url: string): string {
    return url.replace('{{locale}}', this._getLocaleSegment());
  }

  /**
   * Derives the locale segment (e.g. "en-US") from the document's <base href>, which Angular sets
   * per-locale at build time (see angular.json i18n.locales[].baseHref). Falls back to "en-US" for
   * local dev serves that don't set a locale-specific base href (e.g. plain `ng serve`).
   */
  private _getLocaleSegment(): string {
    const basePath = this._document.baseURI.replace(this._document.location.origin, '');
    const match = /^\/([a-z]{2}-[A-Z]{2})\//.exec(basePath);
    return match ? match[1] : 'en-US';
  }

  private _loadLoaderScript$(): Observable<void> {
    const window = this._document.defaultView as any;
    if (window.__vzcChatLoader) {
      return of(undefined);
    }

    const loaderUrl = this._resolveLocaleUrl(this._settings.loaderUrl);

    return new Observable<void>(subscriber => {
      const script = this._document.createElement('script');
      script.src = loaderUrl;
      script.onload = () => {
        subscriber.next();
        subscriber.complete();
      };
      script.onerror = () => {
        subscriber.error(new Error(`[agent-core-chat] Failed to load loader from ${loaderUrl}`));
      };
      this._document.head.appendChild(script);
    });
  }
}
